import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaClipboardList, FaPen } from "react-icons/fa";
import moment from "moment";
import styles from "./Create.module.css";
import AxiosInstance, {
  TokenService,
} from "../../../services/Api";
import BlobImage from "../../../components/BlobImage/BlobImage";
import { fetchLeaveReasonOptions } from "../../../redux/Masterdata";
import {
  CustomButton,
  CustomTextInput,
  CustomRadioGroup,
} from "../../../components";
import PopupModal from "../../../components/PopupModal/PopupModal";

const LeaveApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const editId = location.state?.ID || null;

  const user = TokenService.getUser();
  // const clientData = user?.client_data[0] || {};
  // const staff_id = clientData?.master_id || "";
  const user_id = user?.id || "";

  const { translations } = useSelector((state) => state.language);

  // Get reason options from redux masterData
  const { leaveReasonOptions: reduxReasonOptions } = useSelector(
    (state) => state.masterData,
  );

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Leave detail state
  const [leaveData, setLeaveData] = useState({
    studentName: "",
    fromDate: "",
    toDate: "",
    noDays: "",
    reason: "",
    messaging: "",
    noteMsg: "",
    status: "",
  });

  // Class Teacher Remark — radio selection
  const [remarkStatus, setRemarkStatus] = useState("");
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Build reason dropdown options from redux (for label mapping)
  const reasonOptions = useMemo(
    () =>
      (reduxReasonOptions || []).map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [reduxReasonOptions],
  );

  // Get reason label from value
  const getReasonLabel = useCallback(
    (reasonValue) => {
      const found = reasonOptions.find(
        (opt) => String(opt.value) === String(reasonValue),
      );
      return found ? found.label : reasonValue || "";
    },
    [reasonOptions],
  );

  // Radio options for Class Teacher Remark: Approve=1, Reject=2, Please Attach Medical Certificate=3
  const remarkOptions = useMemo(
    () => [
      {
        value: 1,
        label: translations.labels?.approve || "Approve",
        disabled: false,
      },
      {
        value: 2,
        label: translations.labels?.reject || "Reject",
        disabled: false,
      },
      {
        value: 3,
        label:
          translations.labels?.pleaseAttachMedicalCertificate ||
          "Please Attach Medical Certificate",
        disabled: false,
      },
    ],
    [translations],
  );

  // Fetch reason options from masterData on mount
  useEffect(() => {
    dispatch(fetchLeaveReasonOptions());
  }, [dispatch]);

  // Fetch leave details on mount
  useEffect(() => {
    if (editId) {
      fetchLeaveDetails();
    }
  }, [editId]);

  const fetchLeaveDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/attendance/viewLeave`, {
        id: editId,
      });

      if (response?.data?.status || response?.data?.success) {
        const data = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;

        setLeaveData({
          studentName: data.student_name || "",
          fromDate: data.from_date
            ? moment(data.from_date).format("DD/MM/YYYY")
            : "",
          toDate: data.to_date ? moment(data.to_date).format("DD/MM/YYYY") : "",
          noDays: data.no_days || 0,
          reason: data.reason || "",
          messaging: data.messages || data.messaging || "",
          noteMsg: data.note_msg || data.messages || "",
          status: data.status,
          original_name: data.original_name || "",
          upload_path: data.upload_path || "",
        });

        // If already approved/rejected, pre-select the radio
        if (data.status === 1 || data.status === 2 || data.status === 3) {
          setRemarkStatus(data.status);
        }
      }
    } catch (error) {
      console.error("Error fetching leave details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRadioChange = (name, value) => {
    setRemarkStatus(value);
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!remarkStatus) {
      newErrors.remarkStatus =
        translations.labels?.remarkRequired ||
        "Please select an action (Approve / Reject / Attach Medical Certificate)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const payload = {
        status: parseInt(remarkStatus),
        updated_by: parseInt(user_id),
      };

      const response = await AxiosInstance.put(
        `/attendance/updateLeaveByStaff/${editId}`,
        payload,
      );

      if (response?.data?.status || response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            translations.labels?.leaveApprovalSuccess ||
            "Leave status updated successfully!",
          redirectPath: "/user/leave",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error updating leave:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.leaveApprovalFailed ||
          "Failed to update leave status. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/leave");
  };
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  if (loading) {
    return (
      <>
        <PopupModal
          open={modalState.open}
          title={modalState.type === "success" ? "Success" : "Failed"}
          message={modalState.message}
          onOk={handleSuccessOk}
        />

        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {translations.labels?.leaveRequest || "Leave Request"}
            </h1>
          </div>
          <div className={styles.loadingContainer}>
            <p>{translations.labels?.loading || "Loading..."}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.leaveRequest || "Leave Request"}
          </h1>
        </div>

        {/* Leave Details (Read-only) */}
        <form onSubmit={handleSubmit}>
          {/* Row 1: Student */}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.studentName || "Student"}
                name="studentName"
                type="text"
                value={leaveData.studentName}
                disabled
                readOnly
                required
              />
            </div>
          </div>

          {/* Row 2: From Date, To Date, No. of Days */}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.fromDate || "From Date"}
                name="fromDate"
                type="text"
                value={leaveData.fromDate}
                disabled
                readOnly
                required
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.toDate || "To Date"}
                name="toDate"
                type="text"
                value={leaveData.toDate}
                disabled
                readOnly
                required
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.noOfDaysLeave || "No. of Days"}
                name="noDays"
                type="text"
                value={String(leaveData.noDays)}
                disabled
                readOnly
                required
              />
            </div>
          </div>

          {/* Row 3: Reason, Messaging, Note */}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.reason || "Reason"}
                name="reason"
                type="text"
                value={getReasonLabel(leaveData.reason)}
                disabled
                readOnly
                required
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.messagingLabel || "Messaging"}
                name="messaging"
                type="text"
                value={leaveData.messaging}
                disabled
                readOnly
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.noteLabel || "Note"}
                name="noteMsg"
                type="text"
                value={leaveData.noteMsg}
                disabled
                readOnly
              />
            </div>
            {leaveData?.upload_path && (
              <div className="col-lg-12 px-2 col-md-12 col-sm-12">
                <label className="form-label fw-semibold mb-2">
                  Attachment
                </label>

                <div>
                  {/\.(jpe?g|png|gif)$/i.test(leaveData.upload_path) ? (
                    <BlobImage
                      path={leaveData.upload_path}
                      alt="Attachment"
                      className="img-fluid rounded border"
                      style={{ maxHeight: "300px" }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={async () => {
                        try {
                          const response = await AxiosInstance.get(
                            `/content/image?path=${encodeURIComponent(leaveData.upload_path)}`,
                            { responseType: "blob" },
                          );
                          const objectUrl = URL.createObjectURL(
                            new Blob([response.data], {
                              type:
                                response.headers["content-type"] ||
                                "application/octet-stream",
                            }),
                          );
                          window.open(objectUrl, "_blank");
                        } catch (error) {
                          console.error("Failed to open attachment:", error);
                        }
                      }}
                    >
                      📎 View Attachment
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Class Teacher Remark Section */}
          <div className={styles.header} style={{ marginTop: 24 }}>
            <div className={styles.headerIcon}>
              <FaPen />
            </div>
            <h1 className={styles.headerTitle}>
              {translations.labels?.classTeacherRemark ||
                "Class Teacher Remark"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-12 px-2">
              <CustomRadioGroup
                label={translations.labels?.status || "Status"}
                name="remarkStatus"
                value={remarkStatus}
                onChange={(value) => handleRadioChange("remarkStatus", value)}
                options={remarkOptions}
                required
                error={errors.remarkStatus}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            {leaveData.status !== 4 && (
              <CustomButton
                type="submit"
                variant="contained"
                className={styles.submitButton}
                fullWidth={false}
                loading={submitting}
                loadingText={translations.labels?.submitting || "Submitting..."}
              >
                {translations.labels?.submitButton || "SUBMIT"}
              </CustomButton>
            )}

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancelButton || "CANCEL"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default LeaveApproval;
